import { computed, ref, watch, useStore } from '@nuxtjs/composition-api'
import { debounce } from '@/utils/generalUtils'
import ProgramsService from '@/services/programs'
import { SORT_ENTITIES_ALPHABETICLY_BY_DISPLAYED_VALUE, CATALOG_CONFIGURABLE_SORT_OPTIONS } from '@/constants/featureFlags'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { LIMIT_MEDIUM } from '@/constants/pagination'
import { convertQueryToFilters } from '@/utils/filters'
import usePrint from '@/composables/usePrint'

const LOCAL_SORT_BY_OPTIONS = [
  {
    id: '-',
    name: '-',
  },
  {
    id: 'name',
    name: 'Name',
  },
  {
    id: 'code',
    name: 'Code',
  },
  {
    id: 'longName',
    name: 'Long Name',
  },
  {
    id: 'catalogDisplayName',
    name: 'Catalog Display Name',
  },
  {
    id: 'degreeDesignation',
    name: 'Degree Designation',
  },
  {
    id: 'description',
    name: 'Description',
  },
]

export default function usePrograms(
  root,
  { pageSize = LIMIT_MEDIUM, columns, filtersPayload } = {}
) {
  const {
    DISPLAY_NAME_ENTITY_SORTING_ENABLED,
    useCatalogConfigurableSortOptions
  } = useFeatureFlags({
    DISPLAY_NAME_ENTITY_SORTING_ENABLED: SORT_ENTITIES_ALPHABETICLY_BY_DISPLAYED_VALUE,
    useCatalogConfigurableSortOptions: CATALOG_CONFIGURABLE_SORT_OPTIONS,
  })

  const store = useStore()

  const programSortConfig = computed(() => store.state.settings.programSortConfig)
  const sortByOptions = computed(() => {
    return useCatalogConfigurableSortOptions && programSortConfig.value?.options?.length
      ? programSortConfig.value.options
      : LOCAL_SORT_BY_OPTIONS
  })
  const defaultSortBy = computed(() => {
    return useCatalogConfigurableSortOptions && programSortConfig.value?.default
      ? programSortConfig.value.default
      : sortByOptions.value[0].id !== '-' ? sortByOptions.value[0].id : ''
  })

  const api = new ProgramsService(root.$axios, root.$store)

  const school = root.$store.state.settings.school
  const departments = root.$store.state.departments.all
  const programsFilters = root.$store.state.settings.programsFilters

  const programQuestions = ref({})
  const programs = ref([])

  const { pq, page, department, ...queryFilters } = root.$route.query || {}
  const filters = ref({ ...queryFilters, department })

  const total = ref(0)
  const currentPage = ref(1)
  const isLoading = ref(false)
  const searchQuery = ref(root.$route.query.pq || '')
  const sortBy = ref(root.$route.query.sortBy || defaultSortBy.value || '')

  watch(searchQuery, () => {
    isLoading.value = true
    searchDebounced()
  })

  const { isInPrintMode, printIfInPrintMode } = usePrint(root)

  const departmentFilter = computed({
    get() {
      const department = departments.find(
        (dep) => dep.id === filters.value.department
      )
      return department ? department.id : ''
    },
    set(dep) {
      filters.value.department = dep || ''
    },
  })
  const totalPages = computed(() => {
    return Math.ceil(total.value / pageSize)
  })
  const hasFilters = computed(() => {
    return programsFilters?.filters && programsFilters?.filters.length > 0
  })

  const hasDepartmentFilter = computed(() => {
    return (
      hasFilters.value &&
      programsFilters.filters.some((filter) => {
        return (
          filter.name === 'departments' || filter.id === 'departments-program'
        )
      })
    )
  })

  const programsFormatted = computed(() => {
    return programs.value.map((program) => {
      const director = getDirector(program.contacts)
      return {
        ...program,
        director,
      }
    })
  })

  const searchDebounced = debounce(search, 500)
  async function fetchQuestion() {
    programQuestions.value = await root.$axios.$get(
      `/${school}/general/programTemplate/questions`
    )
  }

  const DEFAULT_SORT_FIELDS_ORDER = DISPLAY_NAME_ENTITY_SORTING_ENABLED
    ? ['catalogDisplayName', 'transcriptDescription', 'longName', 'name']
    : 'name'

  async function fetchData({
    sortBy: defaultSortByParam = DEFAULT_SORT_FIELDS_ORDER,
  } = {}) {
    isLoading.value = true
    const { pq, page = 1, department, ...queryFilters } =
      root.$route.query || {}
    const limit = isInPrintMode.value ? 0 : pageSize
    const skip = Math.max(0, page - 1) * pageSize

    try {
      const departmentFilter = {
        id: 'departments-program',
        name: 'departments',
        inputType: 'text',
        group: 'program',
        type: 'contains',
        value: [department],
      }

      const filtersFromQuery = convertQueryToFilters(
        queryFilters,
        programQuestions,
        filtersPayload
      )

      // Merge default filters with user defined filters set in route query and department filter
      const filtersMerged = {
        condition: 'AND',
        filters: [
          programsFilters || null,
          filtersFromQuery.length > 0 && {
            condition: 'AND',
            filters: filtersFromQuery
          },
          department && departmentFilter
        ].filter(Boolean),
      }

      const { data, listLength } = await api.getPrograms({
        ...(pq && { query: pq }),
        sortBy: sortBy.value || defaultSortByParam , // Some older themes pass sortBy through the function
        skip,
        limit,
        columns,
        filters: filtersMerged,
      })
      programs.value = data
      total.value = listLength
      currentPage.value = page
    } catch (e) {
      this.$bugsnag.notify(e)
      console.warn('Error while fetching search results.')
    } finally {
      isLoading.value = false
      printIfInPrintMode()
    }
  }

  async function queryData({ query = '$filters', limit = 20, skip = 0 }) {
    isLoading.value = true

    try {
      const { data, listLength } = await api.getPrograms({
        query,
        sortBy: sortBy.value || DEFAULT_SORT_FIELDS_ORDER,
        skip,
        limit,
        columns,
        filters: {
          ...(programsFilters || {}),
        },
      })
      programs.value = data
      total.value = listLength
      return data
    } catch (e) {
      this.$bugsnag.notify(e)
      console.warn('Error while fetching search results.')
    } finally {
      isLoading.value = false
    }
  }

  function goToProgram(program) {
    root.$router.push(`/programs/${encodeURIComponent(program.programGroupId)}`)
  }
  function getProgramUrl(program) {
    return `/programs/${encodeURIComponent(program.programGroupId)}`
  }

  function search() {
    root.$router.push({
      name: 'programs',
      query: {
        ...filters.value,
        page: 1,
        pq: searchQuery.value,
      },
    })
  }
  function updatePage(page) {
    root.$router.push({
      name: 'programs',
      query: {
        ...root.$route.query,
        page,
      },
    })
  }

  function updateQuery(query) {
    // This can replace updatePage above in the future
    root.$router.push({
      name: 'programs',
      query: {
        ...root.$route.query,
        ...query,
      },
    })
  }

  function getDirector(contacts) {
    const director = contacts.find((contact) => contact.type === 'director')
    return director ? director.name : ''
  }

  return {
    getDirector,
    updatePage,
    search,
    goToProgram,
    getProgramUrl,
    fetchData,
    queryData,
    searchDebounced,
    programsFormatted,
    hasDepartmentFilter,
    hasFilters,
    totalPages,
    departmentFilter,
    departments,
    programsFilters,
    programs,
    filters,
    total,
    currentPage,
    isLoading,
    searchQuery,
    pageSize,
    fetchQuestion,
    programQuestions,
    sortByOptions,
    defaultSortBy,
    sortBy,
    updateQuery,
  }
}
